import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ApiService from '../../apis/ApiService';
import IconAdd from '../../../common/elements/static/IconAdd.svg';
import IconCancel from '../../../common/elements/static/IconCancel.svg';
import IconAlert from '../../../common/elements/static/IconAlert.svg';

const StyledDiv = styled.div`
  width: ${(props) => props.width};
  background-color: ${(props) => props.backgroundColor};
  border: 1px solid transparent;
  border-color: ${(props) => (props.isInvalid ? '#FA339D' : '')};
  border-radius: 5px;
  letter-spacing: 1px;
  padding: 5px 10px;
  font-size: 12px;
  color: ${(props) => props.textColor};
  display: flex;
  justify-content: ${(props) => props.textAlign};
  align-items: flex-end;
  position: relative;

  &.type-of-line {
    border-radius: 0;
    background: transparent;
    border: none;
    border-bottom: ${(props) => (props.hasValue ? `1px solid ${props.lineColor}` : 'none')};
  }

  &:disabled {
    opacity: 0.3;
  }

  .file-name {
    max-width: calc(100% - 1.2rem);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .cancel-btn {
    position: absolute;
    width: 1.1rem;
    right: 0;
    cursor: pointer;
  }

  .box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    .upload-btn {
      width: 1.5rem;
      margin-top: 0.2rem;
      cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
      opacity: ${(props) => (props.disabled ? 0.3 : 1)};
    }
    &--error-msg {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-bottom: 0.5rem;
      white-space: pre-line;
    }
    &--alert-icon {
      width: 1rem;
      margin-right: 0.2rem;
      margin-bottom: 0.2rem;
    }
  }
`;

const initalError = { isError: false, msg: '' };

const UploadField = (props) => {
  const {
    url,
    name,
    fileName,
    fileNameKey,
    placeholder,
    styleType,
    customBtn,
    maxMB = 1,
    multi = false, // 僅做批次上傳用，請自行處理移除邏輯
    ...restProps
  } = props;
  const [isUploading, setUploading] = useState(false);
  const [errorInfo, setError] = useState(initalError);

  const className = styleType === 'box' ? '' : 'type-of-line';
  const triggerFileInput = useRef();

  function handleReset() {
    triggerFileInput.current.value = '';
    setUploading(false);
    setError(initalError);
    if (multi) {
      props.onChange([]);
      return;
    }
    props.onChange({
      [name]: null,
      url: null,
      path: null,
      [fileNameKey]: null,
    });
  }

  const handleFileOnChange = (e) => {
    e.preventDefault();
    setError(initalError);
    if (e.target.files.length === 0) {
      return;
    }

    setUploading(true);

    for (let file of e.target.files) {
      const { size: fileSize, type } = file;

      const fileType = type.split('/')[0];

      if (fileType !== 'image') {
        setUploading(false);
        setError({
          isError: true,
          msg: '請上傳圖片檔',
        });
        return;
      }
      // 驗證圖片，預設最大 1MB
      if (fileSize > 1024 * 1024 * maxMB) {
        setUploading(false);
        setError({
          isError: true,
          msg: `圖片大小超過${maxMB}MB，\n請重新上傳。`,
        });
        return;
      }
    }

    const fileAmount = e.target.files.length;
    const images = [];

    for (let file of e.target.files) {
      const data = new FormData();
      data.append('media', file);

      const { name: filename, type } = file;

      const fileType = type.split('/')[0];

      data.append('type', fileType);
      ApiService.post(url, { data, withCredentials: true })
        .then((res) => {
          setUploading(false);
          setError(initalError);
          const {
            data: { uniqid, url: fileUrl, path: filePath },
          } = res;
          images.push({
            [name]: uniqid,
            url: fileUrl,
            path: filePath,
            [fileNameKey]: filename,
          });

          if (images.length === fileAmount) {
            if (multi) {
              props.onChange([...images]);
              return;
            }
            props.onChange({
              ...images[0],
            });
          }
        })
        .catch((error) => {
          // 處理上傳失敗
          setUploading(false);
          handleReset();
          setError({
            isError: true,
            msg: '上傳失敗，\n請重新上傳。',
          });
        });
    }
  };

  const uploadBtnOnClick = (e) => {
    e.preventDefault();
    triggerFileInput.current.click();
  };

  return (
    <StyledDiv className={className} hasValue={!!fileName && !errorInfo.isError} {...restProps}>
      {/* 真實的fileupload input */}
      <input
        type="file"
        accept="image/*"
        multiple={multi}
        disabled={props.disabled}
        style={{ display: 'none' }}
        ref={triggerFileInput}
        onChange={handleFileOnChange}
      />

      {/* 顯示檔名 或 上傳中*/}
      {!errorInfo.isError && <div className="file-name">{fileName}</div>}
      {isUploading && <div>上傳中...</div>}

      {/* 移除按鈕 */}
      {!!fileName && !props.disabled && !isUploading && (
        <img className="cancel-btn" src={IconCancel} alt="" onClick={handleReset} />
      )}

      <div className="box">
        {/* 錯誤訊息 */}
        {errorInfo.isError && (
          <div className="box--error-msg">
            <img src={IconAlert} alt="" className="box--alert-icon" />
            {errorInfo.msg}
          </div>
        )}
        {/* 檔案上傳按鈕 */}
        {errorInfo.isError || (!fileName && !isUploading) ? (
          !customBtn ? (
            <img className="upload-btn" onClick={uploadBtnOnClick} src={IconAdd} alt="" />
          ) : (
            <>{customBtn(uploadBtnOnClick)}</>
          )
        ) : null}
      </div>
    </StyledDiv>
  );
};

UploadField.propTypes = {
  /* 框框還是線 */
  styleType: PropTypes.oneOf(['box', 'line']),
  /* 寬度設定 */
  width: PropTypes.string,
};

UploadField.defaultProps = {
  styleType: 'box',
  width: '100%',
  placeholder: null,
  disabled: false,
  onChange: () => {},
  backgroundColor: 'transparent',
  lineColor: '#FFFFFF',
  textAlign: 'center',
  textColor: 'rgba(0, 0, 0, 1)',
  customBtn: null,
};

export default UploadField;
